/**
 * DTO → view-model mapper for HPO phenotypic features.
 *
 * Reads both the BFF shape (snake_case `hpo_id` / `status` / flat
 * `onset` / `severity` / `notes`) and the live PhenoTips `FeatureDTO`
 * (camelCase `termId`, three-state `observed: 'Y' | 'N' | 'U'`, structured
 * qualifier groups for severity / age-of-onset / notes / the PTV2-1169
 * clinical modifiers). The list GET and
 * the POST/PUT responses on `/persons/{guid}/features` both flow through
 * here — mirrors `variant.ts`'s dual-shape strategy.
 *
 * Narrowing policy:
 *   - Unknown `observed` / `status` values collapse to `'unknown'`.
 *   - Qualifier groups are flattened: the first qualifier whose `type`
 *     matches a recognised slot (`SEVERITY`, `AGE_OF_ONSET`, `NOTES`)
 *     populates the corresponding view-model field; later groups for the
 *     same slot are ignored. Free text is preferred over `label`/`termId`.
 */

import type { components } from '@/api/client';
import type {
  FeatureKind,
  FeatureModifiers,
  FeatureOccurrence,
  FeatureStatus,
  FeatureViewModel,
} from '@/features/patient-record/types';

import { narrowEnum, nullableString } from '@/api/mappers/_shared';
import { MODIFIER_SPECS } from '@/features/patient-record/lib/featureModifiers';

type FeatureDto = components['schemas']['Feature'];

const KNOWN_STATUS: ReadonlySet<FeatureStatus> = new Set<FeatureStatus>([
  'present',
  'absent',
  'unknown',
]);

const OBSERVED_TO_STATUS: Readonly<Record<string, FeatureStatus>> = {
  Y: 'present',
  N: 'absent',
  U: 'unknown',
};

const KNOWN_KIND: ReadonlySet<FeatureKind> = new Set<FeatureKind>([
  'PHENOTYPE',
  'PRENATAL_PHENOTYPE',
  'MODE_OF_INHERITANCE',
  'CANCER',
  'DISORDER',
  'CLINICAL_DIAGNOSIS',
  'AGE_OF_ONSET',
]);

const KNOWN_OCCURRENCE: ReadonlySet<FeatureOccurrence> = new Set<FeatureOccurrence>([
  'PRE_APPOINTMENT',
  'POST_APPOINTMENT',
  'BOTH',
]);

function asRecord(dto: FeatureDto): Record<string, unknown> {
  return dto;
}

function pick(r: Record<string, unknown>, snake: string, camel: string): unknown {
  const a = r[snake];
  if (a !== undefined && a !== null) return a;
  const b = r[camel];
  if (b !== undefined && b !== null) return b;
  return undefined;
}

function readHpoId(r: Record<string, unknown>): string {
  return nullableString(pick(r, 'hpo_id', 'termId')) ?? '';
}

function readStatus(r: Record<string, unknown>): FeatureStatus {
  const direct = nullableString(r.status);
  if (direct !== null) {
    return narrowEnum<FeatureStatus>(direct, KNOWN_STATUS, 'unknown');
  }
  const observed = nullableString(r.observed);
  if (observed !== null) {
    return OBSERVED_TO_STATUS[observed] ?? 'unknown';
  }
  return 'unknown';
}

interface QualifierLike {
  readonly type?: unknown;
  readonly label?: unknown;
  readonly stringValue?: unknown;
  readonly termId?: unknown;
}

interface QualifierGroupLike {
  readonly featureQualifiers?: unknown;
}

/**
 * Walk the live `featureQualifierGroups[*].featureQualifiers[*]` tree and
 * return the first qualifier whose `type` matches `kind`. Free text
 * (`stringValue`) wins over `label`, which wins over `termId`.
 */
function findQualifierValue(
  groups: readonly QualifierGroupLike[] | undefined,
  kind: string,
): string | null {
  if (groups === undefined) return null;
  for (const group of groups) {
    const list = group.featureQualifiers;
    if (!Array.isArray(list)) continue;
    for (const q of list as QualifierLike[]) {
      if (q.type !== kind) continue;
      const sv = nullableString(q.stringValue);
      if (sv !== null) return sv;
      const lbl = nullableString(q.label);
      if (lbl !== null) return lbl;
      const tid = nullableString(q.termId);
      if (tid !== null) return tid;
    }
  }
  return null;
}

/**
 * Like {@link findQualifierValue} but returns the qualifier's HPO `termId`
 * (when the qualifier is bound to a modifier term). Returns `null` when no
 * matching group exists or the qualifier is free text. See PTV2-1051 #2 —
 * the row editor uses this to rehydrate the modifier-vocab picker.
 */
function findQualifierTermId(
  groups: readonly QualifierGroupLike[] | undefined,
  kind: string,
): string | null {
  if (groups === undefined) return null;
  for (const group of groups) {
    const list = group.featureQualifiers;
    if (!Array.isArray(list)) continue;
    for (const q of list as QualifierLike[]) {
      if (q.type !== kind) continue;
      const tid = nullableString(q.termId);
      if (tid !== null) return tid;
    }
  }
  return null;
}

function readOnset(r: Record<string, unknown>): string | null {
  const direct = nullableString(r.onset);
  if (direct !== null) return direct;
  const groups = r.featureQualifierGroups;
  return Array.isArray(groups)
    ? findQualifierValue(groups as QualifierGroupLike[], 'AGE_OF_ONSET')
    : null;
}

function readOnsetTermId(r: Record<string, unknown>): string | null {
  const groups = r.featureQualifierGroups;
  return Array.isArray(groups)
    ? findQualifierTermId(groups as QualifierGroupLike[], 'AGE_OF_ONSET')
    : null;
}

function readSeverity(r: Record<string, unknown>): string | null {
  const direct = nullableString(r.severity);
  if (direct !== null) return direct;
  const groups = r.featureQualifierGroups;
  return Array.isArray(groups)
    ? findQualifierValue(groups as QualifierGroupLike[], 'SEVERITY')
    : null;
}

function readSeverityTermId(r: Record<string, unknown>): string | null {
  const groups = r.featureQualifierGroups;
  return Array.isArray(groups)
    ? findQualifierTermId(groups as QualifierGroupLike[], 'SEVERITY')
    : null;
}

function readNotes(r: Record<string, unknown>): string | null {
  const direct = nullableString(r.notes);
  if (direct !== null) return direct;
  const groups = r.featureQualifierGroups;
  return Array.isArray(groups)
    ? findQualifierValue(groups as QualifierGroupLike[], 'NOTES')
    : null;
}

/**
 * Read the PTV2-1169 modifier qualifiers (pace / temporal / spatial /
 * laterality) into a keyed record. Only modifiers with a value are keyed;
 * each keeps its `termId` for picker rehydration (see {@link findQualifierTermId}).
 */
function readModifiers(r: Record<string, unknown>): FeatureModifiers {
  const groups = r.featureQualifierGroups;
  if (!Array.isArray(groups)) return {};
  const list = groups as QualifierGroupLike[];
  const out: Record<string, { label: string; termId: string | null }> = {};
  for (const spec of MODIFIER_SPECS) {
    const label = findQualifierValue(list, spec.wireType);
    if (label === null) continue;
    out[spec.key] = { label, termId: findQualifierTermId(list, spec.wireType) };
  }
  return out;
}

function readOccurrence(r: Record<string, unknown>): FeatureOccurrence | null {
  const groups = r.featureQualifierGroups;
  if (!Array.isArray(groups)) return null;
  const raw = findQualifierValue(groups as QualifierGroupLike[], 'OCCURRENCE');
  if (raw === null) return null;
  return narrowEnum<FeatureOccurrence>(raw, KNOWN_OCCURRENCE, null);
}

function readKind(r: Record<string, unknown>): FeatureKind | null {
  const raw = nullableString(r.type);
  if (raw === null) return null;
  return narrowEnum<FeatureKind>(raw, KNOWN_KIND, null);
}

/** ``{day, month, year}`` triple → partial ISO date (``YYYY[-MM[-DD]]``). */
function readDateObserved(r: Record<string, unknown>): string | null {
  const v = r.dateObserved;
  if (typeof v === 'string') return nullableString(v);
  if (v === null || v === undefined || typeof v !== 'object') return null;
  const o = v as { year?: unknown; month?: unknown; day?: unknown };
  if (typeof o.year !== 'number' || o.year <= 0) return null;
  const parts = [String(o.year).padStart(4, '0')];
  if (typeof o.month === 'number' && o.month > 0) {
    parts.push(String(o.month).padStart(2, '0'));
    if (typeof o.day === 'number' && o.day > 0) {
      parts.push(String(o.day).padStart(2, '0'));
    }
  }
  return parts.join('-');
}

/** Maps a single `Feature` DTO to `FeatureViewModel`. */
export function mapFeature(dto: FeatureDto): FeatureViewModel {
  const r = asRecord(dto);
  return {
    id: nullableString(r.id) ?? '',
    hpoId: readHpoId(r),
    label: nullableString(r.label) ?? '',
    status: readStatus(r),
    kind: readKind(r),
    onset: readOnset(r),
    onsetTermId: readOnsetTermId(r),
    severity: readSeverity(r),
    severityTermId: readSeverityTermId(r),
    modifiers: readModifiers(r),
    occurrence: readOccurrence(r),
    notes: readNotes(r),
    dateObserved: readDateObserved(r),
    createdAt: nullableString(pick(r, 'created_at', 'createdAt')),
  };
}

/** Maps a list of `Feature` DTOs. */
export function mapFeatures(
  dtos: readonly FeatureDto[],
): FeatureViewModel[] {
  return dtos.map(mapFeature);
}
